import { ReactNode, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { baseUrl } from "@kaltura/mediaspace-shared-utils";
import { SessionActivityContext, SessionActivityContextValue } from "./SessionActivityContext";
import { navigateToLogout } from "./navigateToLogout";
import { IdleWarningModal } from "./idle-warning-modal/IdleWarningModal";

export interface SessionActivityProviderProps {
    children?: ReactNode;
}

export const CROSS_TAB_LOGOUT_MESSAGE = "logout";
export const CROSS_TAB_MOUNT_MESSAGE = "mount";
const DEFAULT_LOGOUT_URL = baseUrl + "/user/logout";

/**
 * Wires up cross-tab idle detection and forced logout:
 * - No-ops (renders children only) when the `session` config section is absent.
 * - Otherwise starts react-idle-timer's cross-tab timer from the config's
 *   idleTimeoutMinutes/warningMinutes, and provides SessionActivityContext -
 *   see useSessionActivity - so consumers can heartbeat and read prompt state.
 */
export const SessionActivityProvider = ({ children }: SessionActivityProviderProps) => {
    const { session } = useConfig();

    if (!session) {
        return <>{children}</>;
    }

    return <SessionActivityProviderInner session={session}>{children}</SessionActivityProviderInner>;
};

interface SessionActivityProviderInnerProps {
    session: { idleTimeoutMinutes: number; warningMinutes: number; logoutUrl?: string; keepAliveUrl: string };
    children?: ReactNode;
}

const SessionActivityProviderInner = ({ session, children }: SessionActivityProviderInnerProps) => {
    const [isPrompted, setIsPrompted] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const hasLoggedOutRef = useRef(false);

    // idle warning modal opened
    const onPrompt = useCallback(() => setIsPrompted(true), []);

    // user activity
    const onActive = useCallback(() => setIsPrompted(false), []);

    // onIdle - triggers logout, while keeping current url to reactivate sessions.
    const onIdle = useCallback(() => {
        setIsLoggingOut(true);
        if (hasLoggedOutRef.current) {
            return;
        }
        hasLoggedOutRef.current = true;
        navigateToLogout(session.logoutUrl ?? DEFAULT_LOGOUT_URL);
    }, [session.logoutUrl]);

    // onMessage - activity messages from other tabs
    const onMessage = useCallback(
        (data: unknown) => {
            // logout explicitly done in another tab
            if (data === CROSS_TAB_LOGOUT_MESSAGE) {
                onIdle();
            }
            // another tab mounted: react-idle-timer's own startOnMount broadcast already
            // resets this tab's idle/prompted flags, but never invokes onActive, so a
            // stuck-open modal here needs to be told directly.
            if (data === CROSS_TAB_MOUNT_MESSAGE) {
                onActive();
            }
        },
        [onIdle, onActive]
    );

    const idleTimer = useIdleTimer({
        timeout: session.idleTimeoutMinutes * 60_000,
        promptBeforeIdle: session.warningMinutes * 60_000,
        crossTab: true,
        onPrompt,
        onActive,
        onIdle,
        onMessage,
    });

    // Notifies other tabs that may be idle/prompted that a new tab mounted,
    // so their modal closes instead of staying stuck open.
    const notifyMountOnMount = useEffectEvent(() => idleTimer.message(CROSS_TAB_MOUNT_MESSAGE));
    useEffect(() => {
        notifyMountOnMount();
    }, []);

    // stay logged in - heartbeats the idle timer and pings the server to keep the session alive
    const handleStayLoggedIn = useCallback(() => {
        idleTimer.activate();
        fetch(session.keepAliveUrl, { credentials: "include" }).catch(() => {});
    }, [idleTimer, session.keepAliveUrl]);

    // explicit logout
    const handleLogout = useCallback(() => {
        onIdle();
        idleTimer.message(CROSS_TAB_LOGOUT_MESSAGE);
    }, [idleTimer, onIdle]);

    // tells other tabs to log out, without navigating or changing this tab's own state
    const notifyLogout = useCallback(() => {
        idleTimer.message(CROSS_TAB_LOGOUT_MESSAGE);
    }, [idleTimer]);

    const contextValue: SessionActivityContextValue = useMemo(
        () => ({
            heartbeat: () => idleTimer.activate(),
            isPrompted,
            getRemainingTime: () => idleTimer.getRemainingTime(),
            isEnabled: true,
            notifyLogout,
        }),
        [idleTimer, isPrompted, notifyLogout]
    );

    return (
        <SessionActivityContext.Provider value={contextValue}>
            {children}
            <IdleWarningModal
                open={isPrompted}
                isLoggingOut={isLoggingOut}
                getRemainingTime={contextValue.getRemainingTime}
                onStayLoggedIn={handleStayLoggedIn}
                onLogoutNow={handleLogout}
            />
        </SessionActivityContext.Provider>
    );
};
